import { useState } from "react";
import { FileText, UploadCloud, X } from "lucide-react";
import { TipoArquivo } from "@workflow/types";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { FileUpload } from "@/components/shared/UploadArquivo";
import { fileTypeConfig } from "@/components/shared/upload-arquivo/file-type-config";
import { formatBytes } from "@/components/shared/upload-arquivo/format";
import { Caret, useSequentialTypewriter } from "@/components/shared/typewriter";
import { FormCampoDTO } from "@/types/conclusao.type";

interface CampoArquivoUploadProps {
  campo: FormCampoDTO;
  file: File | null;
  onFileChange: (file: File | null) => void;
  disabled?: boolean;
}

/**
 * Campo ARQUIVO do formulário de conclusão: fora do modal só um gatilho
 * compacto (botão vazio ou linha com o arquivo já anexado); o upload de fato
 * acontece num modal dedicado — é o único jeito de oferecer paste (Ctrl+V)
 * global sem depender de hover nem disputar o Ctrl+V com outro campo da tela.
 */
export function CampoArquivoUpload({
  campo,
  file,
  onFileChange,
  disabled,
}: Readonly<CampoArquivoUploadProps>) {
  const [aberto, setAberto] = useState(false);
  const palette =
    fileTypeConfig[campo.tipoArquivo as TipoArquivo] ?? fileTypeConfig[TipoArquivo.TAREFA];

  const temAjuda = !!campo.ajuda;
  const { typed, isTyping, done } = useSequentialTypewriter(
    temAjuda ? [campo.ajuda!] : [],
    aberto && temAjuda,
    {
      charDelay: 14,
    },
  );
  // Sem ajuda pra digitar, o campo de upload aparece de cara; com ajuda, só
  // depois que o texto termina de "digitar" — evita competir atenção com a animação.
  const podeMostrarUpload = !temAjuda || done;

  return (
    <>
      {file ? (
        <div
          className={cn(
            "flex items-center justify-between gap-2 rounded-lg border px-3 py-2",
            palette.border,
            palette.liteBg,
          )}
        >
          <button
            type="button"
            onClick={() => setAberto(true)}
            disabled={disabled}
            className="flex min-w-0 flex-1 items-center gap-2 text-left"
            title="Clique para trocar o arquivo"
          >
            <FileText className={cn("h-4 w-4 shrink-0", palette.fileIcon)} />
            <span className="min-w-0 truncate text-sm font-medium">{file.name}</span>
            <span className="shrink-0 text-xs text-muted-foreground">{formatBytes(file.size)}</span>
          </button>
          <button
            type="button"
            onClick={() => onFileChange(null)}
            disabled={disabled}
            className="shrink-0 rounded-md p-1.5 text-current opacity-60 transition-colors hover:bg-black/10 hover:opacity-100 dark:hover:bg-white/10"
            title="Remover arquivo"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      ) : (
        <Button
          type="button"
          variant="outline"
          className={cn("w-full justify-start", palette.button)}
          onClick={() => setAberto(true)}
          disabled={disabled}
        >
          <UploadCloud className="mr-2 h-4 w-4" />
          Anexar arquivo
        </Button>
      )}

      <Dialog open={aberto} onOpenChange={setAberto}>
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle>{campo.label}</DialogTitle>
          </DialogHeader>

          {campo.ajuda && (
            <p className="-mt-2 text-sm text-muted-foreground">
              {typed[0]}
              {isTyping(0) && <Caret className="bg-muted-foreground" />}
            </p>
          )}

          {podeMostrarUpload && (
            <FileUpload
              type={(campo.tipoArquivo as TipoArquivo) ?? TipoArquivo.TAREFA}
              variant="default"
              file={file}
              onFileChange={(novoArquivo) => {
                onFileChange(novoArquivo);
                if (novoArquivo) setAberto(false);
              }}
              accept={campo.aceita?.join(",")}
              maxFileSizeMB={campo.tamanhoMaximoMb}
              required={campo.obrigatorio}
              disabled={disabled}
              pasteSempreAtivo
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
